import { Fragment, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { line, centsParam, dayParam, fill, formatCurrency, billDuplicateInvoicePath, mailAccountingFilePath, Size, t, type AvailableCreditRow, type BillAnswer, type BillDuplicateInvoice, type OpenPurchaseOrderRow, type PurchaseOrderRemainder, type SuggestionRow, newDocumentTitle } from '@bms2/shared';
import { DatePicker, Dropdown, Select, TextInput } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Money } from '../../components/Money';
import { ItemTable, hasUnnamedDivider, newLine, savedRows, withDividers, type ItemLineDraft } from '../../components/composites/ItemTable';
import { Modal } from '../../components/modals/Modal';
import { ModalField } from '../../components/modals/ModalShell';
import { useDocumentToday } from '../../shell/ViewerZone';
import { CustomerNotesBox } from './CustomerNotesBox';
import { DocumentEditPage, documentEditStyles as styles } from './DocumentEditPage';
import { BILLS_PATH, PURCHASE_ORDERS_PATH, apiFetch, get, openPurchaseOrdersPath, post, purchaseOrderRemainderPath, useApi, useRows } from './api';
import { useItemPicker } from '../settings/api';
import { useAddNew } from './AddNewHost';
import { ApplyCreditModal, availableCreditPath } from './ApplyCreditModal';

/** The request the form posts from its drafts: only lines carrying an item or free text and a quantity, priced in cents. */
export function billRequestFrom(input: { vendorCompanyId: string; vendorInvoiceNumber: string; billDate: string; paymentTerms: string; sourcePurchaseOrderId: string | null; customerNotes?: string; drafts: readonly ItemLineDraft[]; sources?: ReadonlyMap<string, string> }) {
  // 🔒 The lines the save keeps, and the group dividers at their places among them (decision 0650).
  const { lines, dividers } = savedRows(
    input.drafts,
    (d) => Boolean(d.itemId || (d.freeText && d.itemLabel.trim())) && (d.qty ?? 0) > 0,
    (d) => ({
      itemId: d.itemId, description: d.freeText ? d.itemLabel.trim() : d.description || null, quantity: d.qty ?? 0, unitId: d.unitId ?? null, unitPriceCents: d.rateCents,
      // 🔒 A line filled from the order names the order line it bills and keeps the order line's account (T140; decision 0711).
      ...(input.sources?.get(d.id) ? { sourceLineId: input.sources.get(d.id)!, accountId: d.accountId ?? null } : {}),
    }),
  );
  return {
    vendorCompanyId: input.vendorCompanyId,
    vendorInvoiceNumber: input.vendorInvoiceNumber.trim(),
    billDate: input.billDate,
    paymentTerms: input.paymentTerms.trim() || null,
    sourcePurchaseOrderId: input.sourcePurchaseOrderId,
    customerNotes: input.customerNotes?.trim() || null,
    lines,
    dividers,
  };
}

/**
 * 🔒 The Bill's edit mode (decision 0409), on the Bill's own page rather than
 * in a modal: the vendor, the vendor's own invoice number, the date and the
 * terms, then the lines on the Item Table's purchase column set, then the
 * sticky footer.
 *
 * 🔨 The footer's second control is Submit and never Send. A Bill goes to
 * nobody outside; its first outward move is the approval gate, and a footer
 * reading Send would promise a mail nothing sends.
 *
 * 🔨 The vendor's available credit is shown here, before the Bill exists, so
 * the prompt lands where the Bill is being keyed rather than at the payment
 * run three screens later (piece 24, Vendor credits).
 */
export function BillForm({ billId = null, apiBaseUrl = '' }: { billId?: string | null; apiBaseUrl?: string }) {
  const addNew = useAddNew(apiBaseUrl);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const vendorFromUrl = params.get('vendorId') ?? '';
  const orderFromUrl = params.get('purchaseOrderId') || null;
  // 🔒 T346 (decision 1515): opened from a message in the accounting mailbox, the form carries what the message's document read and files the message once it saves.
  const fromMessage = params.get('fromMessage') || null;
  const numberFromUrl = (params.get('vendorInvoiceNumber') ?? '').slice(0, 80);
  const dateFromUrl = dayParam(params.get('billDate'));
  const amountFromUrl = orderFromUrl ? null : centsParam(params.get('amountCents'));

  const namedVendor = useRows<{ id: string; displayName: string }>(vendorFromUrl ? `${PURCHASE_ORDERS_PATH}/vendors?id=${encodeURIComponent(vendorFromUrl)}` : null, apiBaseUrl);
  const stored = useApi<BillAnswer>(billId ? `${BILLS_PATH}/${billId}` : null, apiBaseUrl);

  const [vendorId, setVendorId] = useState(vendorFromUrl);
  const [vendorName, setVendorName] = useState('');
  const [vendorInvoiceNumber, setVendorInvoiceNumber] = useState(billId ? '' : numberFromUrl);
  // 🔒 Today is the viewer's own day, from the shell's read of their zone, never the machine's clock read as UTC (T114; decisions 0261, 1030).
  const today = useDocumentToday();
  const [billDate, setBillDate] = useState(billId ? today : dateFromUrl ?? today);
  const [paymentTerms, setPaymentTerms] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  // 🔒 A fresh document opens on one empty line, so the table is typed into rather than found (decision 0447).
  // 🔒 A total read off a vendor's document with no order behind it stands as one free-text line, adjustable before the save (decision 1515).
  const [drafts, setDrafts] = useState<ItemLineDraft[]>(() => (amountFromUrl !== null && !billId ? [{ ...newLine(true), itemLabel: line(t('mail.accounting.billLine'), numberFromUrl), rateCents: amountFromUrl }] : [newLine()]));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The vendor's name arrives after the form opened; the state adjusts during the render that sees it.
  const vendorRow = namedVendor.state.kind === 'ready' ? namedVendor.state.data[0] ?? null : null;
  const [seenVendor, setSeenVendor] = useState<string | null>(null);
  if (vendorRow && seenVendor !== vendorRow.id) {
    setSeenVendor(vendorRow.id);
    setVendorId(vendorRow.id);
    setVendorName(vendorRow.displayName);
  }

  // The stored bill fills the form when Edit reopens it, once and never over a later keystroke.
  const held = stored.state.kind === 'ready' ? stored.state.data : null;
  const [seenBill, setSeenBill] = useState<string | null>(null);
  if (held && seenBill !== held.id) {
    setSeenBill(held.id);
    setVendorId(held.vendorCompanyId);
    setVendorName(held.vendorName);
    setVendorInvoiceNumber(held.vendorInvoiceNumber);
    setBillDate(held.billDate);
    setPaymentTerms(held.paymentTerms);
    setCustomerNotes(held.customerNotes ?? '');
    // 🔒 The stored group dividers stand among the lines where they were saved (decision 0650).
    setDrafts(withDividers(held.lines.map((row) => ({
      id: row.id,
      itemId: row.itemId,
      // 🔒 A stored line reopens on its Item's own name, never on its SKU or its description (T118; decision 0640).
      itemLabel: row.itemName ?? row.description,
      sku: row.sku,
      description: row.description,
      qty: row.quantity,
      unit: row.unitName,
      rateCents: row.unitPriceCents,
      freeText: row.itemId === null,
    })), held.dividers));
  }

  /**
   * 🔒 The vendor's open purchase orders (T140; decision 0711): once a vendor
   * is chosen, a new Bill offers every order of theirs with something left to
   * bill, and picking one fills the lines from what is left on it. A stored
   * Bill keeps the order it was raised from.
   */
  const openOrders = useRows<OpenPurchaseOrderRow>(vendorId && !billId ? openPurchaseOrdersPath(vendorId) : null, apiBaseUrl);
  const orderRows = openOrders.state.kind === 'ready' ? openOrders.state.data : [];
  const [orderId, setOrderId] = useState<string | null>(orderFromUrl);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  /** The order line each filled draft bills, keyed by the draft's own id. */
  const [sources, setSources] = useState<ReadonlyMap<string, string>>(() => new Map());

  /** Fills the form from an order's remainder: the lines, each naming the order line it bills, and the vendor the order is for. */
  const applyRemainder = (remainder: PurchaseOrderRemainder): void => {
    const named = new Map<string, string>();
    const filled: ItemLineDraft[] = remainder.lines.map((row, at) => {
      const id = `po-${remainder.purchaseOrderId}-${at}`;
      named.set(id, row.sourceLineId);
      return {
        id, itemId: row.itemId, itemLabel: row.itemName ?? row.description, sku: row.sku, description: row.description,
        qty: row.quantity, unit: row.unitName, unitId: row.unitId, rateCents: row.unitPriceCents, accountId: row.accountId, freeText: row.itemId === null,
      };
    });
    setSources(named);
    setOrderId(remainder.purchaseOrderId);
    setOrderNumber(remainder.displayNumber);
    setVendorId(remainder.vendorCompanyId);
    setVendorName(remainder.vendorName);
    // 🔒 The order's group dividers stand among the lines they still group (decision 0650).
    setDrafts(withDividers(filled, remainder.dividers));
  };

  // 🔒 Opened from a Purchase Order's Create bill, the form fills from that order once, through the same read a pick makes (T136).
  const fromUrl = useApi<PurchaseOrderRemainder>(orderFromUrl && !billId ? purchaseOrderRemainderPath(orderFromUrl) : null, apiBaseUrl);
  const urlRemainder = fromUrl.state.kind === 'ready' ? fromUrl.state.data : null;
  const [seenRemainder, setSeenRemainder] = useState<PurchaseOrderRemainder | null>(null);
  if (urlRemainder && seenRemainder !== urlRemainder) {
    setSeenRemainder(urlRemainder);
    applyRemainder(urlRemainder);
  }
  const urlRefused = fromUrl.state.kind === 'failed';

  const fillFrom = async (purchaseOrderId: string): Promise<void> => {
    const answer = await get<PurchaseOrderRemainder>(purchaseOrderRemainderPath(purchaseOrderId), apiBaseUrl);
    if (!answer.ok) {
      setError(answer.message);
      return;
    }
    applyRemainder(answer.data);
  };

  const pickOrder = (value: string): void => {
    if (!value) {
      // Billing no order: the lines filled from one go with it, and the form opens on one empty line again.
      setOrderId(null);
      setOrderNumber(null);
      setSources(new Map());
      setDrafts([newLine()]);
      return;
    }
    void fillFrom(value);
  };

  const credit = useRows<AvailableCreditRow>(vendorId ? availableCreditPath(vendorId) : null, apiBaseUrl);
  const creditRows = credit.state.kind === 'ready' ? credit.state.data : [];
  // 🔒 T740: View credits lists the vendor's open credits here, where Open the vendor once left an unsaved form for another page.
  const [viewingCredits, setViewingCredits] = useState(false);

  const vendorSearch = async (query: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
    const response = await apiFetch(`${apiBaseUrl}${PURCHASE_ORDERS_PATH}/vendors?q=${encodeURIComponent(query)}`, { signal });
    if (!response.ok) return [];
    const body = (await response.json()) as { rows: { id: string; displayName: string }[] };
    return body.rows.map((row) => ({ id: row.id, line1: row.displayName }));
  };
  const { prices, pickerProps } = useItemPicker(apiBaseUrl, 'purchase');

  /** 🔒 The other live Bill already carrying this vendor's invoice number, set while the person is asked to keep both (T1041; decision 10860). */
  const [duplicate, setDuplicate] = useState<{ then: 'view' | 'submit'; bill: BillDuplicateInvoice } | null>(null);

  const save = async (then: 'view' | 'submit', keepBoth = false): Promise<void> => {
    if (!vendorId) return setError(t('bill.new.needsVendor'));
    if (!vendorInvoiceNumber.trim()) return setError(t('bill.new.needsInvoiceNumber'));
    if (hasUnnamedDivider(drafts)) return setError(t('item.dividerNeedsName'));
    const body = billRequestFrom({
      vendorCompanyId: vendorId,
      vendorInvoiceNumber,
      billDate: billDate || today,
      paymentTerms,
      sourcePurchaseOrderId: held?.sourcePurchaseOrderId ?? orderId,
      customerNotes,
      drafts,
      sources,
    });
    if (body.lines.length === 0) return setError(t('bill.new.needsLines'));
    setBusy(true);
    // 🔒 A vendor invoice number another live Bill of this vendor carries is named, and the person confirms to keep both (T1041; decision 10860).
    if (!keepBoth && !billId) {
      const found = await get<{ duplicate: BillDuplicateInvoice | null }>(billDuplicateInvoicePath(vendorId, body.vendorInvoiceNumber), apiBaseUrl);
      if (found.ok && found.data?.duplicate) {
        setBusy(false);
        return setDuplicate({ then, bill: found.data.duplicate });
      }
    }
    const answer = await post<BillAnswer>(BILLS_PATH, body, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setError(answer.message);
    // 🔒 The message the form was opened from is filed against the new Bill: its PDF attached and its tag set, which marks it dealt with (decision 1515).
    if (fromMessage && !billId) await post(mailAccountingFilePath(fromMessage), { recordType: 'Bill', recordId: answer.data.id }, apiBaseUrl);
    // 🔨 Save and Submit saves first and submits on the saved page, so a refused submission leaves a saved Bill rather than nothing.
    navigate(`/bills/${answer.data.id}${then === 'submit' ? '?act=submit' : ''}`);
  };

  // 🔒 Each credit's figure is the Money component inside the banner's message, which takes a node (decision 0717).
  const creditSentence = creditRows.map((row, index) => (
    <Fragment key={row.id}>{index > 0 ? ' · ' : null}{line(row.kind === 'VendorCredit' ? t('bill.credit.vendorCredit') : t('bill.credit.prepayment'), row.reference, '·')} <Money cents={row.amountCents} currencyCode="USD" /></Fragment>
  ));

  return (
    <>
    <DocumentEditPage
      title={held ? held.billNumber : newDocumentTitle('Bill')}
      subtitle={held ? t('bill.edit.subtitle') : t('bill.new.subtitle')}
      outwardLabel={t('bill.action.submit')}
      busy={busy}
      error={error ?? (urlRefused ? t('bill.po.readFailed') : null)}
      onDismissError={() => setError(null)}
      onSaveDraft={() => void save('view')}
      onSaveAndSend={() => void save('submit')}
      onCancel={() => navigate(billId ? `/bills/${billId}` : '/bills')}
      header={
        <>
          {creditRows.length > 0 ? (
            <div className={styles.wide} data-bill-credit={creditRows.length}>
              {/* 🔒 A bill on this form is not yet posted, so the banner says when the credit applies and offers the list, never an Apply the route refuses (T740). */}
              <AlertBanner tone="warning" message={<>{line(t('bill.credit.heading'), '—')} {creditSentence}{line('.')} {t('bill.credit.applyOnceSubmitted')}</>} action={{ label: t('bill.credit.viewAction'), onAction: () => setViewingCredits(true) }} />
            </div>
          ) : null}
          <div data-bill-field="vendor">
            <Dropdown size={Size.Medium} label={t('bill.field.vendor')} entityLabel={t('bill.field.vendor')} search={vendorSearch} minChars={0} value={vendorName} onSelect={(row) => { setVendorId(row.id); setVendorName(row.line1); if (orderId) pickOrder(''); }} onAddNew={() => addNew.open({ kind: 'record', recordType: 'CompanyRecord', role: 'vendor' }, (row) => { setVendorId(row.id); setVendorName(row.line1); if (orderId) pickOrder(''); })} disabled={vendorFromUrl !== '' && !fromMessage} required />
          </div>
          <ModalField label={t('bill.field.vendorInvoiceNumber')} required>
            <TextInput size={Size.Medium} label={t('bill.field.vendorInvoiceNumber')} labelHidden value={vendorInvoiceNumber} onChange={setVendorInvoiceNumber} name="vendorInvoiceNumber" />
          </ModalField>
          <div data-bill-field="billDate">
            <DatePicker size={Size.Small} label={t('bill.field.billDate')} value={billDate} onChange={setBillDate} mode="date" name="billDate" />
          </div>
          <ModalField label={t('bill.field.paymentTerms')}>
            <TextInput size={Size.Large} label={t('bill.field.paymentTerms')} labelHidden value={paymentTerms} onChange={setPaymentTerms} name="billPaymentTerms" />
          </ModalField>
          {held ? (
            <ModalField label={t('bill.field.purchaseOrder')}>
              <span data-bill-field="purchaseOrder">{held.sourcePurchaseOrderNumber ?? t('bill.new.noPurchaseOrder')}</span>
            </ModalField>
          ) : (
            <div data-bill-field="purchaseOrder" data-bill-open-orders={orderRows.length}>
              <Select
                size={Size.Large}
                label={t('bill.field.purchaseOrder')}
                value={orderId ?? ''}
                onChange={pickOrder}
                placeholder={t('bill.new.noPurchaseOrder')}
                // 🔨 An order opened from its own page is offered even before the list answers, so the field never reads blank beside filled lines.
                options={[
                  ...orderRows.map((row) => ({ value: row.id, label: `${row.displayNumber} · ${formatCurrency({ amount: row.unbilledCents, currencyCode: 'USD' })} ${t('bill.po.leftToBill')}` })),
                  ...(orderId && !orderRows.some((row) => row.id === orderId) ? [{ value: orderId, label: orderNumber ?? orderId }] : []),
                ]}
                disabled={!vendorId}
                hint={!vendorId ? t('bill.po.chooseVendor') : openOrders.state.kind === 'empty' || (openOrders.state.kind === 'ready' && orderRows.length === 0) ? t('bill.po.none') : t('bill.po.hint')}
                dataAttribute={{ 'data-bill-purchase-order': 'true' }}
              />
            </div>
          )}
          <CustomerNotesBox value={customerNotes} onChange={setCustomerNotes} party="vendor" name="bill" />
        </>
      }
      lines={
        <ItemTable
          documentType="Bill"
          columns={['item', 'qty', 'unit', 'rate', 'amount']}
          lines={drafts}
          onChange={setDrafts}
          discountLevel="Footer"
          {...pickerProps()}
          referenceCentsOf={(draft) => (draft.itemId ? prices.current.get(draft.itemId) ?? null : null)}
        />
      }
    />
    {addNew.host}
    {/* 🔨 Unmounted when closed, so a second warning never opens on the first Bill's number. */}
    {duplicate ? (
      <Modal
        open
        title={t('bill.duplicate.title')}
        onCancel={() => setDuplicate(null)}
        dataModal="bill-duplicate-invoice"
        actions={[
          { kind: 'primary', label: t('duplicate.keepBoth'), onAct: () => { const then = duplicate.then; setDuplicate(null); return save(then, true); } },
          { kind: 'cancel', label: t('modal.cancel'), onAct: () => setDuplicate(null) },
        ]}
      >
        <p data-bill-duplicate-invoice={duplicate.bill.billNumber}>{fill(t('bill.duplicate.warning'), { billNumber: duplicate.bill.billNumber, number: vendorInvoiceNumber.trim() })} {t('bill.duplicate.question')}</p>
      </Modal>
    ) : null}
    {/* 🔨 Unmounted when closed and keyed to the vendor, so another vendor's credits never show from the last read. */}
    {viewingCredits && vendorId ? <ApplyCreditModal key={vendorId} source={{ vendorCompanyId: vendorId }} apiBaseUrl={apiBaseUrl} onCancel={() => setViewingCredits(false)} /> : null}
    </>
  );
}
